import { LitElement, html } from 'lit'
import { ModalSaveMixin } from '../core/mixins/modal-save.mixin.js'
import { tagCacheService } from '../core/services/tag-cache.service.js'

/**
 * Modal para criar e editar tags
 * Tags permitem organização adicional de transações
 * Cada tag tem nome e cor para identificação visual
 *
 * Utiliza ModalSaveMixin para integração direta com tagCacheService,
 * controle de loading no botão Salvar, tratamento de erro inline
 * e guard contra fechamento durante operação em andamento.
 *
 * Layout: flexbox com header fixo, conteúdo com scroll independente e footer fixo
 * com botões de ação sempre visíveis (padrão 040-modal-scroll-layout-padronizado)
 */
class ModalTag extends ModalSaveMixin(LitElement) {
  static properties = {
    isEditMode: { type: Boolean },
    tagData: { type: Object }
  }

  /**
   * Identificador da entidade para eventos e data-cy de erro.
   * Usado pelo ModalSaveMixin nos eventos cache-operation-started/finished
   * e no atributo data-cy="erro-salvar-tag".
   * @type {string}
   */
  _entidade = 'tag'

  createRenderRoot () {
    return this
  }

  constructor () {
    super()
    this.open = false
    this.isEditMode = false
    this.tagData = null
    this.originalTheme = null
  }

  /**
   * Fecha o modal restaurando o tema original e delegando ao ModalSaveMixin.
   * O mixin gerencia o guard contra _saving, reset de estado,
   * disparo de evento modal-closed e integração com History API.
   *
   * @param {object} [options={}] - Opções de fechamento repassadas ao mixin base
   * @param {boolean} [options.fromPopstate=false] - Indica se veio do popstate
   */
  close (options = {}) {
    if (this._saving) return

    if (this.originalTheme) {
      document.documentElement.setAttribute('data-theme', this.originalTheme)
      this.originalTheme = null
    }

    super.close(options)
  }

  /**
   * Exibe o modal para criação ou edição de tag.
   * Armazena o tema atual para restauração ao fechar.
   * Em modo de edição, preenche os campos com os dados da tag existente.
   *
   * @param {Object|null} tagData - Dados da tag para edição, ou null para criação
   */
  show (tagData = null) {
    this.isEditMode = !!tagData
    this.tagData = tagData

    this.originalTheme = document.documentElement.getAttribute('data-theme') || 'light'

    this.open = true

    if (this.isEditMode && tagData) {
      setTimeout(() => {
        const nameInput = this.renderRoot.querySelector('[data-cy="input-nome"]')
        const colorInput = this.renderRoot.querySelector('[data-cy="input-cor"]')

        if (nameInput) nameInput.value = tagData.nome
        if (colorInput) colorInput.value = tagData.cor || '#3273dc'
      }, 0)
    }
  }

  /**
   * Fecha o modal ao clicar no backdrop (área escurecida).
   * Delega para this.close() que já gerencia o guard contra _saving.
   */
  _handleBackdropClick () {
    this.close()
  }

  _stopPropagation (e) {
    e.stopPropagation()
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Salvar/Atualizar.
   * Replica o padrão do floating-button com transição suave de cor.
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onSaveHover (e) {
    e.target.style.backgroundColor = 'var(--hover-primary, #7B3EF0)'
  }

  /**
   * Restaura cor original do botão Salvar/Atualizar ao remover o mouse.
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onSaveLeave (e) {
    e.target.style.backgroundColor = ''
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Cancelar.
   * Usa background secundário para destacar o botão neutro.
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onCancelHover (e) {
    e.target.style.backgroundColor = 'var(--glass-bg, rgba(117, 133, 255, 0.22))'
  }

  /**
   * Restaura cor original do botão Cancelar ao remover o mouse.
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onCancelLeave (e) {
    e.target.style.backgroundColor = ''
  }

  /**
   * Executa a operação de salvamento via tagCacheService.
   * Coleta dados do formulário, determina a operação (criar/atualizar)
   * e delega ao _executeSave do ModalSaveMixin que gerencia:
   * - Guard contra clique duplo
   * - Estado de loading (_saving)
   * - Timeout de 30s
   * - Disparo de eventos cache-operation-started/finished
   * - Fechamento após sucesso
   * - Exibição de erro inline após falha
   *
   * @returns {Promise<void>}
   */
  async _handleSave () {
    const nameInput = this.renderRoot.querySelector('[data-cy="input-nome"]')
    const colorInput = this.renderRoot.querySelector('[data-cy="input-cor"]')

    const dados = {
      nome: nameInput.value,
      cor: colorInput.value
    }

    const operacao = this.isEditMode ? 'atualizar' : 'criar'
    const id = this.isEditMode ? this.tagData?.id : undefined

    await this._executeSave(tagCacheService, operacao, dados, id)
  }

  render () {
    return html`
      <div 
        class="modal ${this.open ? 'is-active' : ''}" 
        @click="${this._handleBackdropClick}"
        style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 20000;
          display: ${this.open ? 'flex' : 'none'};
          align-items: center;
          justify-content: center;
        "
      >
        <div 
          class="card"
          @click="${this._stopPropagation}"
          style="
            width: 90%;
            max-width: 500px;
            max-height: 85vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border-radius: var(--radius-card, 20px);
            box-shadow: var(--shadow-card, 0 10px 40px rgba(0,0,0,0.35));
            position: relative;
          "
        >
          <!-- Zona 1: Header fixo no topo com sombra sobre o conteúdo -->
          <header class="card-header" style="flex-shrink: 0; position: relative; z-index: 2; box-shadow: var(--shadow-header-modal, 0 4px 12px rgba(0,0,0,0.3));">
            ${this.renderBotaoVoltarIOS()}
            <p class="card-header-title" data-cy="modal-tag-title">
              ${this.isEditMode ? 'Editar Tag' : 'Adicionar Tag'}
            </p>
            ${this.renderBotaoFechar('modal-tag-close')}
          </header>

          <!-- Zona 2: Conteúdo com scroll independente -->
          <div class="card-content custom-scrollbar" style="
            flex: 1;
            overflow-y: auto;
            overflow-x: hidden;
            min-height: 0;
          ">
            <div class="field mb-4">
              <label class="label is-size-7 has-text-weight-medium">Nome</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  placeholder="Ex: Urgente"
                  required
                  data-cy="input-nome"
                  @input="${this._clearError}"
                  @change="${this._clearError}"
                />
              </div>
            </div>

            <div class="field mb-4">
              <label class="label is-size-7 has-text-weight-medium">Cor</label>
              <div class="control">
                <input
                  class="input"
                  type="color"
                  value="#3273dc"
                  data-cy="input-cor"
                  @input="${this._clearError}"
                  @change="${this._clearError}"
                />
              </div>
            </div>

            <!-- Mensagem de erro inline posicionada abaixo dos campos e acima do footer -->
            ${this.renderErroSalvar()}
          </div>

          <!-- Zona 3: Footer fixo com botões de ação e sombra sobre o conteúdo -->
          <footer class="card-footer"
            data-cy="footer-modal-tag"
            aria-label="Ações do modal de tag"
            style="
              flex-shrink: 0;
              padding: 1rem 1.5rem;
              background-color: var(--bg-surface, #151C30);
              border-top: 1px solid var(--border, #26304A);
              position: relative;
              z-index: 2;
              box-shadow: var(--shadow-footer-modal, 0 -4px 12px rgba(0,0,0,0.3));
            "
          >
            <div class="field is-grouped is-justify-content-flex-end" style="width: 100%">
              <div class="control">
                <button 
                  class="button" 
                  type="button" 
                  data-cy="btn-cancelar-tag"
                  ?disabled="${this._saving}"
                  @click="${this.close}"
                  @mouseenter="${this._onCancelHover}"
                  @mouseleave="${this._onCancelLeave}"
                >
                  Cancelar
                </button>
              </div>
              <div class="control">
                <button 
                  class="button is-primary ${this._saving ? 'is-loading' : ''}" 
                  type="button" 
                  data-cy="btn-salvar-tag"
                  ?disabled="${this._saving}"
                  @click="${this._handleSave}"
                  @mouseenter="${this._onSaveHover}"
                  @mouseleave="${this._onSaveLeave}"
                >
                  ${this.isEditMode ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </div>
          </footer>
        </div>
      </div>
    `
  }
}

customElements.define('modal-tag', ModalTag)
